/**
 * Shared React Query options for hooks across the app.
 */

import { keepPreviousData } from "@tanstack/react-query";

/**
 * Default options for any list/search query.
 *
 * `placeholderData: keepPreviousData` is critical for any list page whose
 * search/filter state drives the query key: without it, every keystroke
 * flips `isLoading` to true, which makes the page early-return its
 * skeleton, which unmounts the search input — focus is lost and the
 * table flickers between every keystroke. With it, the previous rows
 * stay rendered while the new query is in flight and the search input
 * stays mounted (and focused).
 *
 * Spread this at the start of `useQuery({...})` so the hook's explicit
 * options (queryKey, queryFn, staleTime, enabled, etc.) take precedence
 * over the defaults:
 *
 *     return useQuery({
 *       ...LIST_QUERY_OPTIONS,
 *       queryKey: ...,
 *       queryFn: ...,
 *     });
 *
 * Apply only to list queries that take filter params. Detail queries
 * (keyed by a single id) don't have this problem and don't need it.
 */
export const LIST_QUERY_OPTIONS = {
  placeholderData: keepPreviousData,
} as const;
